import BitmapFontData, {
  BitmapFontFile,
  BitmapFontPackage,
} from '@/model/BitmapFont/BitmapFontData'
import type MainProject from '@/model/MainProject'
import DataFileMap from '@/model/Resource/DataFileMap'
import {
  buildBitmapFontData as generatorBuildBitmapFontData,
  clampBitmapFontSize,
  toBitmapFontPackageKey,
} from '@/features/bitmap-font/generator'

export const BITMAP_FONT_FOLDER_SEGMENTS = ['位图']
export const BITMAP_FONT_URL_PREFIX = 'cgbitmap://'
export const DEFAULT_BITMAP_FONT_TEXT =
  '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$%^&*()-_=+/□: '

/**
 * 按展示名与字号查找工程内已生成的位图字体包。
 */
export function findBitmapFontPackage(
  project: MainProject | null | undefined,
  fontName: string,
  fontSize: number,
): BitmapFontPackage | null {
  const cleanName = String(fontName || '').trim()
  const cleanSize = clampBitmapFontSize(Number(fontSize || 0))
  if (!project?.bitmapFont || !cleanName || !cleanSize) return null
  const requestKey = toBitmapFontPackageKey(cleanName, cleanSize)
  return (
    project.bitmapFont.packages.find(
      (item) => toBitmapFontPackageKey(item.fontName, item.fontSize) === requestKey,
    ) ?? null
  )
}

function syncBitmapFontRoot(root: DataFileMap | null, bitmapFont: BitmapFontData | null): DataFileMap | null {
  if (!root && !bitmapFont) return root
  const nextRoot = root ? new DataFileMap(root.toJson()) : DataFileMap.categoryRoot('字体')
  let folder =
    nextRoot.children.find((item) => item.isFolder && item.name === BITMAP_FONT_FOLDER_SEGMENTS[0]) ?? null
  if (!folder) {
    folder = DataFileMap.categoryRoot(BITMAP_FONT_FOLDER_SEGMENTS[0])
    nextRoot.children.push(folder)
  }
  folder.children = []
  if (bitmapFont) {
    for (const file of bitmapFont.files) {
      folder.children.push(DataFileMap.fileLeaf(file.fileName, toBitmapFontVirtualUrl(file.fileName)))
    }
  }
  return nextRoot
}

export function toBitmapFontVirtualUrl(fileName: string): string {
  return `${BITMAP_FONT_URL_PREFIX}${encodeURIComponent(fileName)}`
}

export function isBitmapFontVirtualUrl(url: string): boolean {
  return String(url || '').startsWith(BITMAP_FONT_URL_PREFIX)
}

function bitmapFontFileNameFromUrl(url: string): string {
  return decodeURIComponent(String(url || '').slice(BITMAP_FONT_URL_PREFIX.length))
}

export function resolveBitmapFontPreviewUrl(project: MainProject | null, url: string): string {
  if (!project?.bitmapFont || !isBitmapFontVirtualUrl(url)) return ''
  const fileName = bitmapFontFileNameFromUrl(url)
  const file = project.bitmapFont.files.find((item) => item.fileName === fileName) ?? null
  if (!file) return ''
  return file.ensureObjectUrl()
}

/**
 * 为工程生成位图字体并同步虚拟资源树「位图」目录（测试/发布前调用）。
 */
export async function generateProjectBitmapFont(params: {
  project: MainProject
  currentRoot: DataFileMap | null
  extraText?: string
}): Promise<{ bitmapFont: BitmapFontData; root: DataFileMap | null }> {
  const previous = params.project.bitmapFont
  const bitmapFont = await generatorBuildBitmapFontData({
    project: params.project,
    currentRoot: params.currentRoot,
    extraText: params.extraText,
    existingBitmapFont: previous,
  })
  if (previous && previous !== bitmapFont) {
    previous.dispose()
  }
  params.project.bitmapFont = bitmapFont
  return {
    bitmapFont,
    root: syncBitmapFontRoot(params.currentRoot, bitmapFont),
  }
}

/** 清空内存位图包，并同步清空虚拟资源树「位图」目录。 */
export function clearProjectBitmapFont(params: {
  project: MainProject | null
  currentRoot: DataFileMap | null
}): Promise<DataFileMap | null> {
  if (params.project?.bitmapFont) {
    params.project.bitmapFont.dispose()
    params.project.bitmapFont = null
  }
  return Promise.resolve(syncBitmapFontRoot(params.currentRoot, null))
}
